import { useEffect, useState } from 'react';

import '../agente/Agente.css'

// Bibliotecas de terceiros
import { Box, Divider, FormControl, InputLabel, MenuItem, Select, TextField, ThemeProvider, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify'

// Componentes personalizados
import Loading from '../../components/loading/Loading';

// DTOs
import { Usuario } from '../../dto/Usuario';
import { UserInfo } from '../../dto/UserInfo';

// Serviços
import { EmpresaServices } from '../../services/EmpresaService';

// Helpers
import { mostraMensagemErro, mostraMensagemSucesso } from '../../helpers/Notificacoes';
import BarraMenu from '../../components/barraMenu/BarraMenu';
import { ModoBarra } from '../../components/utils';
import { UsuarioServices } from '../../services/UsuarioService';
import { Empresa } from '../../dto/Empresa';
import SelectPersonalizado from '../../components/inputs/inputSelect';
import MensagemVerificacaoSenha from '../../components/mensagemVerificacaoSenha/mensagemVerificacaoSenha';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { validarSenha } from '../../helpers/utils';
import { temaEdicao, temaVisualizacao } from '../../components/theme/Theme';


interface CadUsuarioProps {
    modoInicial?: ModoBarra;
    usuarioSelecionado?: any;
}

const CadUsuario: React.FC<CadUsuarioProps> = ({ modoInicial, usuarioSelecionado }) => {

    // Navegação / rota
    const navigate = useNavigate();
    //const location = useLocation();

    // Services
    const usuarioService = new UsuarioServices();
    const empresaService = new EmpresaServices();

    // User info
    const [userInfo] = useState<UserInfo>(JSON.parse(localStorage.getItem('udbs')!));

    // Constantes fixas
    const DADOS_VAZIOS: Usuario = {
        id: '',
        email: '',
        passwordHash: '',
        sessionId: null,
        codAgente: '',
        nome: '',
        idEmpresa: '',
        ativo: 'S',
        agentes: [],
    };

    // Estados principais do usuario
    const [usuarioOriginal, setUsuarioOriginal] = useState<Usuario | null>(null);
    const [usuario, setUsuario] = useState<Usuario>(usuarioSelecionado ?? DADOS_VAZIOS);
    const [empresas, setEmpresas] = useState<Empresa[] | []>([]);
    const [empresaSelecionada, setEmpresaSelecionada] = useState<Empresa | null>(null);
    const [buscouEmpresas, setBuscouEmpresas] = useState<boolean>(false);
    const [password, setPassword] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [confirmPassword, setConfirmPassword] = useState('');

    // Estado de edição
    const [modoEdicao, setModoEdicao] = useState<ModoBarra>(modoInicial ?? ModoBarra.leitura);
    const isEditavel = modoEdicao === ModoBarra.edicaoregistro || modoEdicao === ModoBarra.novoregistro;
    const [dadosEditados, setDadosEditados] = useState(false);
    const [tema, setTema] = useState(temaVisualizacao);

    // Estado de interface
    const [isLoading, setIsLoading] = useState(false);

    // Datas
    const [dataLancamento] = useState(() => {
        const dataAtual = new Date();
        return dataAtual.toLocaleString("pt-BR");
    });

    function verificaAlteracoes() {
        if (usuario !== usuarioOriginal) {
            setDadosEditados(true);
        }

        return false;
    }

    async function buscaEmpresas() {
        setIsLoading(true);

        const response = await empresaService.get(`/v1/empresas`);
        if (response.codigo !== 200) {
            if (response.codigo === 404) {
                setBuscouEmpresas(true);
                setErro('Não há registros');
            } else {
                setErro(response.mensagem);
            }

            setIsLoading(false);
        } else {
            setErro('');
            setEmpresas(response.status ?? []);
            setIsLoading(false);
            setBuscouEmpresas(true);
        }
    }

    useEffect(() => {
        const validarSenhas = () => {
            if (password === '') {
                setErro(null);
                return;
            }

            const senhaValida = validarSenha(password);
            if (!senhaValida.status) {
                setErro(senhaValida.error ? senhaValida.error : 'Senha inválida!');
                return;
            }

            setErro(null);

            if (confirmPassword === '') {
                return;
            }

            if (password !== confirmPassword) {
                setErro('As senhas não coincidem.');
            }
        };

        validarSenhas();
    }, [password, confirmPassword]);


    useEffect(() => {
        const buscaDados = async () => {
            setIsLoading(true);

            if (empresas.length === 0) {
                buscaEmpresas();
            }

            if (usuario.idEmpresa !== '') {
                const empresaFilter = empresas.find((empresa: Empresa) => empresa.idEmpresa === usuario.idEmpresa)
                setEmpresaSelecionada(empresaFilter!)
            }

            setIsLoading(false);
        };
        buscaDados();
    }, [empresas, usuario.idEmpresa]);


    function updatePage(page: string, espera?: boolean) {

        if (espera) {
            setTimeout(() => {
                navigate("/mvp", {
                    state: {
                        propsPage: page
                    }
                });
            }, 3000);
        } else {
            navigate("/mvp", {
                state: {
                    propsPage: page
                }
            });
        }
    };


    const salvarUsuario = async () => {
        setIsLoading(true);

        try {
            const cadastroUsuario: Usuario = {
                id: usuario?.id,
                email: usuario.email,
                passwordHash: password,
                sessionId: null,
                nome: usuario.nome,
                idEmpresa: empresaSelecionada!.idEmpresa!,
                ativo: usuario.ativo || 'S',
                agentes: usuario.agentes,
                codAgente: usuario.codAgente
            };

            const usuarioSaved = await usuarioService.put(`/v1/usuario`, undefined, [cadastroUsuario]);
            if (usuarioSaved.codigo === 200) {
                mostraMensagemSucesso("Usuário cadastrado com sucesso");

                // Atualiza estado com o usuario salvo
                setUsuario(cadastroUsuario);
                setUsuarioOriginal(cadastroUsuario);

                if (modoInicial === ModoBarra.edicao) {
                    setModoEdicao(ModoBarra.edicao); // mostra botão "Editar"
                } else {
                    setModoEdicao(ModoBarra.insercao); // mostra botão "Novo"
                }

            } else {
                mostraMensagemErro(usuarioSaved.mensagem);
            }
            setIsLoading(false);

        } catch (error: any) {
            console.error('Erro ao salvar usuário:', error);
            mostraMensagemErro('Erro ao salvar usuário:' + error.erro);
            return;

        } finally {
            setIsLoading(false);
        }
    };

    function handleSelectEmpresa(id: string) {
        const empresaFilter = empresas.find((empresa: Empresa) => empresa.idEmpresa === id)
        setEmpresaSelecionada(empresaFilter as Empresa);
    };

    function voltar() {
        if (dadosEditados) {
            const confirma = window.confirm('Os dados preenchidos serão perdidos');

            if (confirma) {
                updatePage('usuarios');
            }
            else return;
        }
        updatePage('usuarios');
    };

    function EditarUsuario() {
        setUsuarioOriginal(usuario); // guarda o estado original
        setModoEdicao(ModoBarra.edicaoregistro);
        setTema(temaEdicao);
    }

    function NovoUsuario() {
        setUsuarioOriginal(null); // limpa estado original
        setUsuario(DADOS_VAZIOS);
        setModoEdicao(ModoBarra.novoregistro);
        setTema(temaEdicao);
    }

    function Cancelar() {
        if ((modoEdicao === ModoBarra.edicaoregistro || modoEdicao === ModoBarra.novoregistro) && usuarioOriginal) {
            setUsuarioOriginal(usuarioOriginal!);
            setModoEdicao(ModoBarra.edicao);
            setTema(temaVisualizacao);

        } else {
            setUsuario(DADOS_VAZIOS);
            setModoEdicao(ModoBarra.leitura);
        }
    }



    return (
        <div className="paginaCadastro">
            <ThemeProvider theme={tema}>
                <Box className='containerFormCadastro'>
                    <Paper elevation={0} className='usuario-paper-header'>
                        <Box className='usuario-cad-header-row'>
                            <Box className='usuario-cad-title-wrap'>
                                <Box className='usuario-cad-title-icon'>
                                    <PersonRoundedIcon sx={{ color: '#0965c1', fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography className='usuario-cad-title'>
                                        Cadastro de Usuário
                                    </Typography>
                                    <Typography className='usuario-cad-subtitle'>
                                        Gerencie as informações e dados do usuário cadastrado.
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className='usuario-cad-actions'>
                                <BarraMenu
                                    modoAtual={modoEdicao}
                                    onAcao={(acao) => {
                                        if (acao === "edicaoregistro") EditarUsuario();
                                        if (acao === "cancelar" || acao === "voltar") Cancelar();
                                        if (acao === "novoregistro") NovoUsuario();
                                        if (acao === "sair") voltar();
                                        if (acao === "salvar") salvarUsuario();
                                    }}
                                />
                            </Box>
                        </Box>
                    </Paper>
                    <Box className='usuarios-form-card'>
                        {isLoading && <Loading />}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <Box>
                                <Typography className='usuario-cad-title'>
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                        <PersonRoundedIcon sx={{ color: '#0965c1', fontSize: 18 }} />
                                        Dados do Usuário
                                    </Box>
                                </Typography>
                                <Divider className='usuarios-divider' />
                            </Box>
                            {/* Linha 1: Código, Nome, Ativo */}
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
                                <TextField
                                    label="Código agente"
                                    sx={{ width: 150 }}
                                    size="small"
                                    onChange={(e) => setUsuario((prevUsuario) => ({
                                        ...prevUsuario,
                                        codAgente: e.target.value
                                    }))}
                                    value={usuario.codAgente}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={modoEdicao === 'novoregistro' ? false : true}
                                />
                                <TextField
                                    label="Nome"
                                    value={usuario.nome || ''}
                                    sx={{ width: 650 }}
                                    size="small"
                                    onChange={(e) => setUsuario((prevUsuario) => ({
                                        ...prevUsuario,
                                        nome: e.target.value
                                    }))}
                                    required
                                    disabled={!isEditavel}
                                />
                                {/* ATIVO */}
                                <FormControl
                                    sx={{
                                        width: 150,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff'
                                        },
                                        '& .MuiInputLabel-root': {
                                            backgroundColor: '#fff',
                                            padding: '0 5px'
                                        }
                                    }}>

                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={usuario.ativo}
                                        disabled={true}
                                        
                                        sx={{ height: 35, minHeight: 32, '.MuiSelect-select': { py: '7px !important', minHeight: '0 !important' } }}
                                    >
                                        <MenuItem key="ativo" value="S">
                                            Ativo
                                        </MenuItem>
                                        <MenuItem key="inativo" value="N">
                                            Inativo
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Linha 2: Email, Empresa */}
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
                                <TextField
                                    label="Email"
                                    value={usuario.email || ''}
                                    variant="outlined"
                                    sx={{ width: 660 }}
                                    size="small"
                                    onChange={(e) => setUsuario((prevUsuario) => ({
                                        ...prevUsuario,
                                        email: e.target.value
                                    }))}
                                    required
                                    disabled={!isEditavel}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <FormControl sx={{
                                    height: 25, 
                                    width: 315,
                                    minWidth: 300,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#fff'
                                    },
                                    '& .MuiInputLabel-root': {
                                        backgroundColor: '#fff',
                                        padding: '0 5px !important',
                                        fontSize: '12px'
                                    }
                                }}>
                                  
                                <InputLabel shrink>Empresa</InputLabel>
                                    <SelectPersonalizado
                                        value={empresaSelecionada?.idEmpresa ?? ''}
                                        label={"Empresa"}
                                       
                                        onChange={(valor) => handleSelectEmpresa((valor))}
                                       options={empresas.map((item) => ({
                                            value: item.idEmpresa!,
                                            label: item.descricao,
                                        }))}
                                        disabled={!isEditavel}
                                        sx={{ height: 25, minHeight: 25, '.MuiSelect-select': { py: '7px !important', minHeight: '0 !important' } }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200, // altura máxima da janela do menu
                                                    overflowY: 'auto'
                                                }
                                            }
                                        }} />

                                </FormControl>

                            </Box>
                            {/* Linha 3: Senha, Confirme a Senha */}
                            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <TextField
                                    className="fields"
                                    required
                                    id="filled-password-input"
                                    label="Senha"
                                    type="password"
                                    autoComplete="current-password"
                                    variant="outlined"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    sx={{ width: 483 }}
                                    size="small"
                                    disabled={!isEditavel}
                                />
                                <TextField
                                    className="fields"
                                    required
                                    id="filled-confirm-password-input"
                                    label="Confirme a Senha"
                                    type="password"
                                    autoComplete="current-password"
                                    variant="outlined"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    sx={{ width: 483 }}
                                    size="small"
                                    disabled={!isEditavel}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                                {isEditavel && (
                                    <MensagemVerificacaoSenha erros={erro!} />
                                )}
                            </Box>
                        </Box>
                        <ToastContainer />
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}

export default CadUsuario;
